import {
  Box,
  Checkbox,
  Divider,
  FormControlLabel,
  Typography,
} from "@mui/material";
import FormAutocomplete from "../controls/FormAutocomplete.jsx";
import FormDatePicker from "../controls/FormDatePicker.jsx";
import FormNumberField from "../controls/FormNumberField.jsx";
import FormRadioGroup from "../controls/FormRadioGroup.jsx";
import FormSelect from "../controls/FormSelect.jsx";
import FormSwitch from "../controls/FormSwitch.jsx";
import FormTextField from "../controls/FormTextField.jsx";
import FormTimePicker from "../controls/FormTimePicker.jsx";
import { useFormEditMode } from "./contexts.js";
import CheckboxControl from "../controls/CheckboxControl.jsx";
import StepperControl from "../controls/StepperControl.jsx";
import AccordionControl from "../controls/AccordionControl.jsx";
import SBWarningControl from "../controls/SBWarningControl.jsx";
import ClaimAttachmentsControl from "../controls/ClaimAttachmentsControl.jsx";
import ClaimCostsEditorControl from "../controls/ClaimCostsEditorControl.jsx";
import CoverageTypeQuestionsControl from "../controls/CoverageTypeQuestionsControl.jsx";
import { useConditional } from "./useConditional.js";
import { useSelectOptions } from "./useSelectOptions.js";
import { useClaimCauseOptions } from "./useClaimCauseOptions.js";

export default function ControlFactory({ item }) {
  const isDisabled = useConditional(item.disabled) || !useFormEditMode();
  const { options: fetchedOptions, isLoading } = useSelectOptions(item);
  const options = useClaimCauseOptions(item, fetchedOptions);

  switch (item.control) {
    case "input":
    case "emailinput":
    case "password":
      return (
        <FormTextField
          name={item.name}
          label={item.label}
          type={item.control === "password" ? "password" : "text"}
          disabled={isDisabled}
          fullWidth
        />
      );
    case "textarea":
      return (
        <FormTextField
          name={item.name}
          label={item.label}
          multiline
          rows={item.rows ?? 4}
          disabled={isDisabled}
          fullWidth
        />
      );
    case "select":
      return (
        <FormSelect
          name={item.name}
          label={item.label}
          options={options}
          disabled={isDisabled}
        />
      );
    case "datepicker":
      return (
        <FormDatePicker
          name={item.name}
          label={item.label}
          disabled={isDisabled}
        />
      );
    case "timepicker":
      return (
        <FormTimePicker
          name={item.name}
          label={item.label}
          disabled={isDisabled}
        />
      );
    case "switch":
      return (
        <FormSwitch name={item.name} label={item.label} disabled={isDisabled} />
      );
    case "checkbox":
      return <CheckboxControl item={item} />;
    case "numberinput":
    case "sliderinput":
      return (
        <FormNumberField
          name={item.name}
          label={item.label}
          unit={item.unit}
          disabled={isDisabled}
          fullWidth
        />
      );
    case "autocomplete":
      return (
        <FormAutocomplete
          name={item.name}
          label={item.label}
          options={options}
          disabled={isDisabled}
          loading={isLoading}
          transformValue={(o) => o?.key ?? null}
          findOptionByValue={(value, opts) =>
            opts.find((o) => o.key === value) ?? null
          }
        />
      );
    case "radiogroup":
      return (
        <FormRadioGroup
          name={item.name}
          label={item.label}
          options={(options.length ? options : (item.options ?? [])).map(
            (o) => ({
              value: o.key ?? o.value,
              label: o.label,
            })
          )}
          disabled={isDisabled}
        />
      );
    case "info":
      return (
        <Box sx={{ py: 1 }}>
          {item.label && (
            <Typography variant="subtitle2">{item.label}</Typography>
          )}
          {item.text && (
            <Typography variant="body2" color="text.secondary">
              {item.text}
            </Typography>
          )}
        </Box>
      );
    case "sectiontitle":
      return (
        <Divider textAlign="left" sx={{ mt: 1 }}>
          <Typography variant="subtitle1" fontWeight="medium">
            {item.label}
          </Typography>
        </Divider>
      );
    case "stepper":
      return <StepperControl item={item} />;
    case "accordion":
      return <AccordionControl item={item} />;
    case "sbwarning":
      return <SBWarningControl item={item} />;
    case "claim_attachments":
      return <ClaimAttachmentsControl item={item} disabled={isDisabled} />;
    case "claim_costs_editor":
      return <ClaimCostsEditorControl item={item} />;
    case "coverage_type_questions":
      return <CoverageTypeQuestionsControl item={item} />;
    default:
      return (
        <Typography variant="caption" color="warning.main" component="div">
          [Nicht implementiert: {item.control}]
        </Typography>
      );
  }
}
